<template>
  <!--
    «Подтверждение семьи» in the asked member's chat (docs/cabinet-redesign/04 §0.2 З-7): support asks the member to
    vouch for a family member who lost access to the app. «Подтверждаю» asks for the member's own confirmation (passkey,
    app PIN or an e-mail code); «Это не он» is asked twice — both answers are final.
  -->
  <div class="cb iv" :class="{ 'cb--muted': closed }" data-testid="chat-block-IDENTITY_VOUCH" :data-state="v?.state">
    <template v-if="v">
      <div class="iv__head">
        <span class="iv__title">{{ t('cabinet.vouch.title') }}</span>
        <DsStatusChip :tone="vouchTone(v.state)" :label="t(vouchStateKey(v))" data-testid="identity-vouch-state" />
      </div>
      <p class="cb__text iv__lead" data-testid="identity-vouch-text">
        {{ t('cabinet.vouch.text', { name: v.subjectName ?? t('cabinet.vouch.someone') }) }}
      </p>

      <template v-if="v.canAnswer">
        <div class="cb__notice" data-testid="identity-vouch-warning">
          <div class="cb__noticeTitle">{{ t('cabinet.vouch.warnTitle') }}</div>
          <div class="cb__noticeBody">{{ t('cabinet.vouch.warnBody') }}</div>
        </div>
        <div class="cb__row">
          <small>{{ t('cabinet.vouch.until') }}</small>
          <span data-testid="identity-vouch-expires">{{ dateTime(v.expiresAt) }}</span>
        </div>

        <template v-if="declining">
          <p class="cb__hint" data-testid="identity-vouch-decline-confirm">{{ t('cabinet.vouch.declineSure') }}</p>
          <button type="button" class="cb__danger" :disabled="disabled" data-testid="identity-vouch-decline-yes"
                  @click="decline">{{ t('cabinet.vouch.declineYes') }}</button>
          <button type="button" class="cb__secondary cb__secondary--wide" :disabled="disabled"
                  @click="declining = false">{{ t('common.back') }}</button>
        </template>
        <template v-else>
          <button type="button" class="cb__primary" :disabled="disabled" data-testid="identity-vouch-confirm"
                  @click="confirm">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
            {{ t('cabinet.vouch.confirm') }}
          </button>
          <button type="button" class="cb__danger" :disabled="disabled" data-testid="identity-vouch-decline"
                  @click="declining = true">{{ t('cabinet.vouch.decline') }}</button>
        </template>
      </template>

      <p v-else-if="answerKey" class="cb__state" data-testid="identity-vouch-answer">{{ t(answerKey) }}</p>
      <p v-else class="cb__state" data-testid="identity-vouch-closed">{{ t('cabinet.vouch.notAnswered') }}</p>
    </template>
    <p v-else class="cb__text">{{ t('cabinet.vouch.gone') }}</p>
    <p v-if="error" class="cb__err" data-testid="chat-block-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { identityVouchApi } from '@/api/identityVouch';
import type { StepUpFactor } from '@/api/stepup';
import type { BlockState } from '@/api/support';
import DsStatusChip from '@/components/ds/DsStatusChip.vue';
import { useStepUp } from '@/composables/useStepUp';
import { stepUpFactors } from '@/views/cabinet/master/masterUi';
import { myAnswerKey, vouchClosed, vouchStateKey, vouchTone } from '@/views/cabinet/vouch/vouchUi';
import { useCardAction, useCardFormat } from './useCardAction';

const props = defineProps<{
  state?: Extract<BlockState, { type: 'IDENTITY_VOUCH' }>;
  busy?: boolean;
  reread: () => Promise<void> | void;
}>();

const { t } = useI18n();
const { dateTime } = useCardFormat();
const { runStepUp } = useStepUp();
const { running, error, run } = useCardAction(() => props.reread());

const v = computed(() => props.state?.vouch ?? null);
const closed = computed(() => !!v.value && vouchClosed(v.value));
const answerKey = computed(() => (v.value ? myAnswerKey(v.value) : null));
const disabled = computed(() => !!props.busy || running.value);
const declining = ref(false);

/** «Подтверждаю»: the first answer is 403 STEP_UP_REQUIRED (after every other check) — then the chooser and the grant. */
function confirm(): void {
  const id = v.value?.id;
  if (!id) return;
  void run(async () => {
    try {
      await identityVouchApi.confirm(id);
    } catch (e) {
      const factors = stepUpFactors<StepUpFactor>(e);
      if (!factors) throw e;
      let grant: string;
      try {
        grant = await runStepUp('IDENTITY_VOUCH', factors);
      } catch (cancelled) {
        if ((cancelled as Error)?.name === 'StepUpCancelled') return;      // dismissed — nothing answered
        throw cancelled;
      }
      await identityVouchApi.confirm(id, grant);
    }
  });
}

function decline(): void {
  const id = v.value?.id;
  if (id) void run(async () => { await identityVouchApi.decline(id); declining.value = false; });
}
</script>

<style scoped lang="scss">
@use './cards.scss';

.iv__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.iv__title { font-family: var(--font-heading); font-size: 19px; line-height: 1.25; color: var(--onyx); }
.iv__lead { margin-top: 8px; }
</style>
